import { Button, FormControl, Grid, MenuItem, Paper, Select, TextareaAutosize, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PersonIcon from '@mui/icons-material/Person';
import { Edit, Email, LocationOn, Phone, Delete } from "@mui/icons-material";
import UserImage from "../../../components/Assets/images/user-profile.jpg";
import Input from "../../../components/common/input/Input"
import { useDispatch, useSelector} from "react-redux";
import axiosInstance from "../../../utils/axios/axiosInstance";
import API from "../../../config/apiconfig";
import { userApiLoader } from "../../../action/loaderActions";
import { toast } from "react-toastify";
import { userLoginSuccess, userLogout } from "../../../action/userActions";
import { useNavigate } from "react-router-dom";

const Myprofile = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.user);
    const [image, setImage] = useState(UserImage); 
    const [isEdit,setIsEdit] = useState(false);
    const [isDeletePopupOpen,setIsDeletePopupOpen] = useState(false);
    const [isUniversityList,setIsUniversityList] = useState(null);
    const [phoneError, setPhoneError] = useState('');
    const [nameError, setNameError] = useState('');
    const [userDetails, setUserDetails] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        address: '',
        degree: '',
        university: '',
        image: null,
        user_id: ''
    });

    // Profile API endpoint
    const PROFILE_API_URL = `${process.env.REACT_APP_USER_MANAGEMENT_API_BASE_URL}/v1/profile`;

    const validatePhoneNumber = (phone) => {
        const phoneRegex = /^[+]?[\d\s\-\(\)]{10,15}$/;
        const digitsOnly = phone.replace(/\D/g, '');
        
        if (!phone.trim()) {
            return 'Phone number is required';
        }
        if (!phoneRegex.test(phone)) {
            return 'Please enter a valid phone number';
        }
        if (digitsOnly.length < 10 || digitsOnly.length > 15) {
            return 'Phone number must be between 10-15 digits';
        }
        return '';
    };

    const validateName = (name) => {
        const nameRegex = /^[a-zA-Z\s]+$/;
        
        if (!name.trim()) {
            return 'Full name is required';
        }
        if (!nameRegex.test(name)) {
            return 'Name should only contain alphabets and spaces';
        }
        if (name.trim().length < 2) {
            return 'Name must be at least 2 characters long';
        }
        return '';
    };

    const handleDelete = () => {
        dispatch(userApiLoader({isApiLoader: true}));
        axiosInstance.delete(
            `${process.env.REACT_APP_USER_MANAGEMENT_API_BASE_URL}/v1/delete-account`
        )
        .then((res) => {
            console.log('Account deleted successfully:', res.data);
            toast.success(res?.data?.message || 'Your account has been deleted successfully.', {
                autoClose: 6000,
            });
            dispatch(userLogout());
            navigate('/');
        })
        .catch((err) => {
            // Enhanced error logging for debugging
            console.error('Delete API Error:', {
                status: err?.response?.status,
                message: err?.response?.data?.message,
                fullError: err?.response?.data
            });
            
            // Show user-friendly error message
            const errorMessage = err?.response?.status === 500 
                ? 'Server error. Please contact support or try again later.'
                : err?.response?.data?.message || 'Failed to delete account. Please try again.';
                
            toast.error(errorMessage, {
                autoClose: 6000,
            });
            dispatch(userApiLoader({isApiLoader: false}));
        })
    }

    const handleImageDelete = () => {
        dispatch(userApiLoader({isApiLoader: true}));
        
        // Use the dedicated DELETE API endpoint for profile picture deletion
        axiosInstance.delete(PROFILE_API_URL)
        .then((res) => {
            console.log('Profile picture deleted successfully:', res.data);
            
            // Show success message from API response or default
            const successMessage = res?.data?.message || 'Profile picture deleted successfully!';
            toast.success(successMessage, {
                autoClose: 6000,
            });
            
            // Immediately update userDetails to null to trigger useEffect
            setUserDetails(prev => ({
                ...prev, 
                image: null
            }));
            
            // Also immediately set the default image for instant UI feedback
            setImage(UserImage);
            
            dispatch(userApiLoader({isApiLoader: false}));
        })
        .catch((err) => {
            // Enhanced error logging for debugging
            console.error('Delete Profile Picture API Error:', {
                status: err?.response?.status,
                message: err?.response?.data?.message,
                fullError: err?.response?.data
            });
            
            // Show user-friendly error message
            const errorMessage = err?.response?.status === 500 
                ? 'Server error. Please contact support or try again later.'
                : err?.response?.data?.message || 'Failed to delete profile picture. Please try again.';
                
            toast.error(errorMessage, {
                autoClose: 6000,
            });
            
            dispatch(userApiLoader({isApiLoader: false}));
        })
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImage(reader.result); 
            };
            reader.readAsDataURL(file); 
            const formData = new FormData();
            formData.append('name', userDetails.fullName); 
            formData.append('phone_number', userDetails.phoneNumber); 
            formData.append('address', userDetails.address); 
            formData.append('degree', userDetails.degree); 
            formData.append('campus', userDetails.university?.campus_id || userDetails.university); 
            formData.append('profile_picture', file); 
            console.log(typeof file);
            
            dispatch(userApiLoader({isApiLoader: true}));
            axiosInstance.post(
                PROFILE_API_URL,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            )
            .then((res) => {
                console.log('Profile updated successfully:', res.data);
                toast.success('Profile picture updated successfully!', {
                    autoClose: 6000,
                });
                getUserDetails();
            })
            .catch((err) => {
                toast.error(err?.response?.data?.message || 'Failed to update profile picture', {
                    autoClose: 6000,
                });
                console.error('Error updating profile:', err);
                dispatch(userApiLoader({isApiLoader: false}));
                // Reset to previous image on error
                setImage(userDetails.image || UserImage);
            })
        }
    };

    const handleChange = (field, value) => {
        setUserDetails(prevState => ({
            ...prevState,
            [field]: value
        }));
        
        // Validate phone number on change
        if (field === 'phoneNumber' && isEdit) {
            const error = validatePhoneNumber(value);
            setPhoneError(error);
        }
        
        // Validate name on change
        if (field === 'fullName' && isEdit) {
            const error = validateName(value);
            setNameError(error);
        }
    };   
    
    const handleSave = () => {
        // Validate name before saving
        const nameValidationError = validateName(userDetails.fullName);
        if (nameValidationError) {
            setNameError(nameValidationError);
            toast.error(nameValidationError, {
                autoClose: 6000,
            });
            return;
        }

        // Validate phone number before saving
        const phoneValidationError = validatePhoneNumber(userDetails.phoneNumber);
        if (phoneValidationError) {
            setPhoneError(phoneValidationError);
            toast.error(phoneValidationError, {
                autoClose: 6000,
            });
            return;
        }

        setIsEdit(false);
        setPhoneError('');
        setNameError('');
        console.log(typeof userDetails.university);
        
        const data = { 	
            "name": userDetails.fullName,
            "phone_number": userDetails.phoneNumber,
            "address": userDetails.address,
            "degree": userDetails.degree,
            "campus": typeof userDetails.university === 'number' ? userDetails.university : userDetails.university?.campus_id,
        }
        
        dispatch(userApiLoader({isApiLoader: true}));
        axiosInstance.post(
            PROFILE_API_URL,
            data,
        )
        .then((res) => {
            console.log('Profile updated successfully:', res.data);
            toast.success('Profile updated successfully!', {
                autoClose: 6000,
            });
            getUserDetails();
        })
        .catch((err) => {
            console.error('Error updating profile:', err);
            dispatch(userApiLoader({isApiLoader: false}));
            toast.error(err?.response?.data?.message || 'Failed to update profile', {
                autoClose: 6000,
            });
        })
    }

    const getUniverSityList = () => {
        axiosInstance.get(
            `${API.USER_MANAGEMENT_API_BASE_URL}${API.CAMPUS_LIST}`
        )
        .then((res) => {
            setIsUniversityList(res?.data?.data);
        })
        .catch((err) => {
            console.log(err?.response?.data || err?.data);
        })
    }

    const getUserDetails = () => {
        dispatch(userApiLoader({isApiLoader: true}));
        
        // First try to get profile using the new API endpoint
        axiosInstance.get(PROFILE_API_URL)
        .then((res) => {
            // Handle the response format: {"status": 200, "data": {...}}
            const profileData = res?.data?.data;
            const { user_id, profile_picture, name, phone_number, degree, campus, email, address } = profileData;
            
            setUserDetails({
                fullName: name || '',
                email: email || '',
                phoneNumber: phone_number || '',
                address: address || '',
                degree: degree || '',
                university: campus || '',
                image: profile_picture || null, // Ensure null if no image
                user_id: user_id
            });
            
            dispatch(userLoginSuccess({...currentUser, UserId: user_id}));
            dispatch(userApiLoader({isApiLoader: false}));
        })
        .catch((err) => {
            console.error('Error fetching profile:', err);
            
            // Fallback to the old API if the new one fails
            axiosInstance.get(
                `${API.USER_MANAGEMENT_API_BASE_URL}${API.PROFILE_DETAILS}`
            )
            .then((res) => {
                const { user_id, profile_picture, name, phone_number, degree, campus, email, address } = res?.data?.data;
                setUserDetails({
                    fullName: name || '',
                    email: email || '',
                    phoneNumber: phone_number || '',
                    address: address || '',
                    degree: degree || '',
                    university: campus || '',
                    image: profile_picture || null, // Ensure null if no image
                    user_id: user_id
                });
                
                dispatch(userLoginSuccess({...currentUser, UserId: user_id}));
                dispatch(userApiLoader({isApiLoader: false}));
            })
            .catch((fallbackErr) => {
                console.error('Error with fallback API:', fallbackErr);
                toast.error('Failed to load profile data', {
                    autoClose: 6000,
                });
                dispatch(userApiLoader({isApiLoader: false}));
            });
        })
    }

    useEffect(() => {
        getUserDetails();
        getUniverSityList();
    },[])

    // Improved useEffect for image synchronization
    useEffect(() => {
        console.log('userDetails.image changed:', userDetails.image);
        
        if (userDetails.image) {
            // User has a custom profile picture
            console.log('Setting custom image:', userDetails.image);
            setImage(userDetails.image);
        } else {
            // No custom image or image was deleted - use default
            console.log('Setting default image');
            setImage(UserImage);
        }
    }, [userDetails.image]);

    // Debug logging
    console.log('Current userDetails.image:', userDetails.image);
    console.log('Current image state:', image);
    console.log('Delete button should show:', !!userDetails.image);
    
    console.log(userDetails);
    

    return (
        <div className="container" style={{ 
           
            fontFamily: '"Saira", sans-serif'
        }}>
            
            <div className="css-11ezll3-MuiToolbar-root breadcrumbs" style={{position: 'relative', top: '-50px'}}>
                <div className="top-sec">

        <div className="row">
            <div className="col-md-4">
<Paper 
                        elevation={2} 
                        style={{
                            borderRadius: '12px',
                            padding: '32px',
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            fontFamily: '"Saira", sans-serif'
                        }}
                    >
                        {/* Profile Image Section */}
                        <div className="flex justify-center mb-8">
                            <div className="relative group">
                                <div style={{
                                    width: '140px',
                                    height: '140px',
                                    borderRadius: '50%',
                                    padding: '4px',
                                    background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                                }}>
                                    <img 
                                        src={image} 
                                        alt="User" 
                                        style={{
                                            width: '132px',
                                            height: '132px',
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            border: '3px solid white',
                                        }}
                                    />
                                </div>
                                <label 
                                    htmlFor="file-upload" 
                                    style={{
                                        position: 'absolute',
                                        bottom: '8px',
                                        right: '8px',
                                        background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '44px',
                                        height: '44px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                        transition: 'all 0.3s ease',
                                    }}
                                    className="hover:scale-110"
                                >
                                    <Edit fontSize="small"/>
                                </label>
                                {/* Delete Button - Only show if user has a custom profile picture */}
                                {userDetails.image && (
                                    <button 
                                        onClick={handleImageDelete}
                                        style={{
                                            position: 'absolute',
                                            bottom: '8px',
                                            left: '8px',
                                            background: 'linear-gradient(45deg, #dc2626 0%, #ef4444 100%)',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '44px',
                                            height: '44px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            border: 'none',
                                            boxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)',
                                            transition: 'all 0.3s ease',
                                        }}
                                        className="hover:scale-110"
                                        title="Delete profile picture"
                                    >
                                        <Delete fontSize="small"/>
                                    </button>
                                )}
                                <input
                                    type="file"
                                    id="file-upload"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        {/* User Info Cards */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {[
                                { icon: PersonIcon, label: 'Full name', value: userDetails.fullName },
                                { icon: Email, label: 'Email', value: userDetails.email },
                                { icon: Phone, label: 'Phone number', value: userDetails.phoneNumber },
                                { icon: LocationOn, label: 'Address', value: userDetails.address }
                            ].map((item, index) => (
                                <div key={index} style={{
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    border: '1px solid #e2e8f0',
                                }}>
                                    <div className="flex gap-3 items-center mb-2">
                                        <div style={{
                                            background: '#ce55b9',
                                            borderRadius: '6px',
                                            padding: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <item.icon style={{ color: 'white', fontSize: '16px' }} />
                                        </div>
                                        <p style={{ 
                                            fontWeight: '600', 
                                            color: '#1e293b',
                                            fontSize: '14px',
                                            margin: 0,
                                            fontFamily: '"Saira", sans-serif'
                                        }}>{item.label}</p>
                                    </div>
                                    <p style={{
                                        marginLeft: '36px',
                                        color: '#64748b',
                                        fontSize: '14px',
                                        wordBreak: 'break-words',
                                        margin: 0,
                                        lineHeight: '1.4',
                                        fontFamily: '"Saira", sans-serif'
                                    }}>
                                        {item.value || 'Not specified'}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Delete Account Button */}
                        <div className="flex justify-center mt-8">
                            <Button 
                                variant="outlined" 
                                onClick={() => setIsDeletePopupOpen(true)}
                                style={{
                                    background: 'white',
                                    color: '#dc2626',
                                    border: '1px solid #dc2626',
                                    borderRadius: '8px',
                                    padding: '10px 20px',
                                    fontWeight: '600',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontFamily: '"Saira", sans-serif',
                                    transition: 'all 0.2s ease'
                                }}
                                className="hover:bg-red-50"
                            >
                                Delete Account
                            </Button>
                        </div>
                    </Paper>
            </div>

            <div className="col-md-8">
                                <Grid item xs={12} md={8}>
                    <Paper 
                        elevation={2} 
                        style={{
                            borderRadius: '12px',
                            padding: '32px',
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            fontFamily: '"Saira", sans-serif'
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-8">
                            <Typography 
                                variant="h5" 
                                style={{ 
                                    fontWeight: '700',
                                    color: '#ce55b9',
                                    fontFamily: '"Saira", sans-serif'
                                }}
                            >
                                Personal Information
                            </Typography>
                            {!isEdit && (
                                <Button
                                    onClick={() => setIsEdit(true)}
                                    style={{
                                        background: '#ce55b9',
                                        color: 'white',
                                        borderRadius: '8px',
                                        padding: '8px 16px',
                                        textTransform: 'none',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: '"Saira", sans-serif',
                                        transition: 'all 0.2s ease'
                                    }}
                                    className="hover:bg-opacity-90"
                                >
                                    <Edit style={{ width: '16px', height: '16px' }} />
                                    Edit Profile
                                </Button>
                            )}
                        </div>

                        {/* Form Fields */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <div className="flex flex-col gap-1">
                                        <Input 
                                            disabled={!isEdit} 
                                            type="text" 
                                            label="Full Name" 
                                            value={userDetails.fullName} 
                                            onChange={(e) => handleChange('fullName', e.target.value)} 
                                        />
                                        {isEdit && nameError && (
                                            <span style={{
                                                color: '#dc2626',
                                                fontSize: '12px',
                                                marginTop: '4px',
                                                fontFamily: '"Saira", sans-serif'
                                            }}>
                                                {nameError}
                                            </span>
                                        )}
                                    </div>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <div className="flex flex-col gap-1">
                                        <Input 
                                            disabled={!isEdit} 
                                            type="text" 
                                            label="Phone Number" 
                                            value={userDetails.phoneNumber} 
                                            onChange={(e) => handleChange('phoneNumber', e.target.value)}
                                        />
                                        {isEdit && phoneError && (
                                            <span style={{
                                                color: '#dc2626',
                                                fontSize: '12px',
                                                marginTop: '4px',
                                                fontFamily: '"Saira", sans-serif'
                                            }}>
                                                {phoneError}
                                            </span>
                                        )}
                                    </div>
                                </Grid>
                            </Grid>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <div className="flex flex-col gap-1">
                                        <Input 
                                            disabled={!isEdit} 
                                            type="text" 
                                            label="Degree" 
                                            value={userDetails.degree} 
                                            onChange={(e) => handleChange('degree', e.target.value)}
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <div className="flex flex-col gap-1">
                                        {isEdit ? (
                                            <>
                                              
                                                <FormControl fullWidth>
                                                   <Input 
                                                disabled="disabled"
                                                type="text" 
                                                label="University/Campus" 
                                                value={userDetails.university?.campus_name}
                                            />
                                                </FormControl>
                                            </>
                                        ) : (
                                            <Input 
                                                disabled={!isEdit} 
                                                type="text" 
                                                label="University/Campus" 
                                                value={userDetails.university?.campus_name || userDetails.university}
                                            />
                                        )}
                                    </div>
                                </Grid>
                            </Grid>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <div className="flex flex-col gap-1">
                                        <Input 
                                            disabled={true} 
                                            type="text" 
                                            label="Email" 
                                            value={userDetails.email}
                                        />
                                    </div>
                                </Grid>
                            </Grid>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <label style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#1e293b',
                                        marginBottom: '8px',
                                        display: 'block',
                                        fontFamily: '"Saira", sans-serif'
                                    }}>
                                        Address
                                    </label>
                                    <TextareaAutosize 
                                        style={{
                                            width: '100%',
                                            minHeight: '60px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            fontSize: '14px',
                                            fontFamily: '"Saira", sans-serif',
                                            resize: 'vertical',
                                            outline: 'none',
                                            background: isEdit ? 'white' : '#f8fafc',
                                            color: '#1e293b',
                                            transition: 'all 0.2s ease'
                                        }}
                                        className={isEdit ? 'focus:border-pink-500' : ''}
                                        minRows={3}
                                        value={userDetails.address}
                                        readOnly={!isEdit}
                                        disabled={!isEdit}
                                        onChange={(e) => handleChange('address', e.target.value)}
                                        placeholder="Enter your address..."
                                    />
                                </Grid>
                            </Grid>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end mt-8">
                            <div className="flex items-center gap-4">
                                <Button 
                                    onClick={() => {
                                        setIsEdit(false);
                                        setPhoneError('');
                                        setNameError('');
                                        getUserDetails(); // Reset form to original values
                                    }}
                                    disabled={!isEdit}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: '600',
                                        border: '1px solid #e2e8f0',
                                        color: '#64748b',
                                        background: 'white',
                                        opacity: isEdit ? 1 : 0.5,
                                        fontFamily: '"Saira", sans-serif',
                                        transition: 'all 0.2s ease'
                                    }}
                                    className="hover:bg-gray-50"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSave}
                                    disabled={!isEdit}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: '600',
                                        background: isEdit ? '#ce55b9' : '#e2e8f0',
                                        color: isEdit ? 'white' : '#9ca3af',
                                        fontFamily: '"Saira", sans-serif',
                                        transition: 'all 0.2s ease'
                                    }}
                                    className={isEdit ? 'hover:bg-opacity-90' : ''}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    </Paper>
                </Grid>
                
            </div>
        </div>


            <Grid container spacing={3}>
               

                {/* Personal Information Form */}
                

                {/* Delete Confirmation Modal */}
                {isDeletePopupOpen && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: '"Saira", sans-serif'
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '32px',
                            maxWidth: '400px',
                            width: '90%',
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '50%',
                                    background: '#fee2e2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px auto'
                                }}>
                                    <span style={{ fontSize: '24px' }}>⚠️</span>
                                </div>
                                <Typography variant="h6" style={{ 
                                    fontWeight: '700', 
                                    color: '#1e293b', 
                                    marginBottom: '8px',
                                    fontFamily: '"Saira", sans-serif'
                                }}>
                                    Delete Account
                                </Typography>
                                <p style={{ 
                                    color: '#64748b', 
                                    fontSize: '14px', 
                                    lineHeight: '1.5',
                                    margin: 0,
                                    fontFamily: '"Saira", sans-serif'
                                }}>
                                    Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed.
                                </p>
                            </div>
                            
                            <div className="flex justify-center gap-4">
                                <Button 
                                    onClick={() => setIsDeletePopupOpen(false)}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: '600',
                                        border: '1px solid #e2e8f0',
                                        color: '#64748b',
                                        background: 'white',
                                        fontFamily: '"Saira", sans-serif',
                                        transition: 'all 0.2s ease'
                                    }}
                                    className="hover:bg-gray-50"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    onClick={handleDelete}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: '600',
                                        background: '#dc2626',
                                        color: 'white',
                                        fontFamily: '"Saira", sans-serif',
                                        transition: 'all 0.2s ease'
                                    }}
                                    className="hover:bg-opacity-90"
                                >
                                    Delete Account
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Grid>

            <style jsx>{`
                .hover\\:bg-opacity-90:hover {
                    background-color: rgba(206, 85, 185, 0.9) !important;
                }
                
                .hover\\:bg-red-50:hover {
                    background-color: #fef2f2 !important;
                }
                
                .hover\\:bg-gray-50:hover {
                    background-color: #f9fafb !important;
                }
                
                .focus\\:border-pink-500:focus {
                    border-color: #ce55b9 !important;
                    box-shadow: 0 0 0 2px rgba(206, 85, 185, 0.1) !important;
                }

                .hover\\:scale-110:hover {
                    transform: scale(1.1) !important;
                }
            `}</style>
        </div>
        </div>
        </div>
    );
}

export default Myprofile;